/**
 * DropdownButton.tsx - Reusable Dropdown Button Component
 * 
 * This component provides a reusable dropdown button interface used throughout
 * the fundraiser builder for various selection and action menus.
 * 
 * Key Features:
 * - Customizable button text and icon
 * - Dropdown menu with selectable options
 * - Click-outside detection for closing
 * - Keyboard navigation support
 * - Flexible option rendering
 * - Loading and disabled states
 * 
 * Usage Patterns:
 * - Category selection in builder forms
 * - Action menus with multiple options
 * - Status selection dropdowns
 * - Configuration option selection
 * 
 * Props:
 * - buttonText: string - Text displayed on the button
 * - options: Array<{value: string, label: string}> - Dropdown options
 * - selectedValue?: string - Currently selected option value
 * - onSelect: (value: string) => void - Selection change handler
 * - disabled?: boolean - Whether button is disabled
 * - loading?: boolean - Whether to show loading state
 * - icon?: React.ReactNode - Optional icon for button
 * 
 * Dependencies: Styled-components, Lucide icons, click-outside detection
 */

import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { ChevronDown, User } from 'lucide-react';
import { sanitizeHtml } from "@mindmemobile/mm-payment-modules";

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 24px;
  background: linear-gradient(135deg, #10b981, #059669);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(16, 185, 129, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ChevronIcon = styled(ChevronDown)<{ isOpen: boolean }>`
  transition: transform 0.2s ease;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const DropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 1000;
  min-width: 272px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.isOpen ? 'translateY(4px)' : 'translateY(-4px)'};
  transition: all 0.2s ease;
  margin-top: 4px;
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: #ffffff;
  color: #1e293b;
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 12px;

  &:hover {
    background: #f8fafc;
  }

  &:not(:last-child) {
    border-bottom: 1px solid #f1f5f9;
  }
`;

const ItemIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
`;

const ItemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ItemTitle = styled.div`
  font-weight: 600;
  color: #1e293b;
`;

const ItemDescription = styled.div`
  font-size: 12px;
  color: #64748b;
  line-height: 1.3;
  
  /* Allow HTML line breaks in descriptions */
  white-space: pre-line;
`;

interface DropdownOption {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  onClick: () => void;
}

interface AddPromoterDropdownProps {
  options: DropdownOption[];
}

export const AddPromoterDropdown: React.FC<AddPromoterDropdownProps> = ({ options }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (onClick: () => void) => {
    onClick();
    setIsOpen(false);
  };

  return (
    <DropdownContainer ref={dropdownRef}>
      <DropdownButton onClick={handleToggle} type="button">
        <User size={16} />
        Add Promoters
        <ChevronIcon size={16} isOpen={isOpen} />
      </DropdownButton>

      <DropdownMenu isOpen={isOpen}>
        {options.map(option => (
          <DropdownItem
            key={option.id}
            type="button"
            onClick={() => handleOptionClick(option.onClick)}
          >
            <ItemIcon>{option.icon}</ItemIcon>
            <ItemContent>
              <ItemTitle>{option.title}</ItemTitle>
              <ItemDescription dangerouslySetInnerHTML={{ __html: sanitizeHtml(option.description) }} />
            </ItemContent>
          </DropdownItem>
        ))}
      </DropdownMenu>
    </DropdownContainer>
  );
};